import { useState, useContext, createContext, ReactNode } from "react";
import axios from "axios";
import { useNavigate, useLocation } from 'react-router-dom'
import { loginURL, refreshTokenURL, logoutURL } from "../constants";
import { notification } from "antd";
import Cookies from 'js-cookie';
interface AuthContextType {
  login: (user: string, password: string) => void; // Replace 'string' with the actual type of user if needed
  logout: () => void;
  getStoredToken: () => string | any;
  getUser: () => string | any;
  tryRefresh: () => Promise<boolean>;
  getCookie: (name: string) => string | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}


export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate()
  const location = useLocation()  

  const login = async (user: string, password: string) => {
    // Check if there's already a different user logged in
    const currentUser = getUser();
    const shouldClearIntegrationCookies = currentUser && currentUser !== user;

    axios.post(loginURL, { username: user, password: password })
      .then(async (tokens) => {
        // Clear integration cookies if logging in as a different user
        if (shouldClearIntegrationCookies) {
          Cookies.remove('azuread_token', { path: '/' });
          Cookies.remove('slack_access_token', { path: '/' });
          Cookies.remove('fireflies_access_token', { path: '/' });
        }

        window.localStorage.setItem("tokens", JSON.stringify(tokens.data));
        window.localStorage.setItem("user", user);
        const redirectPath = location.state?.path || '/'
        navigate(redirectPath, { replace: true })
      })
      .catch((error) => {
        console.log(error.response.data);
        notification.error({
          message: 'Login Failed',
          description: error.response.data.detail || 'An error occurred during login.',
        });
      });
  };


  const getUser = () => {
    const user = window.localStorage.getItem("user");
    return user ? user : "";
  };

  const getStoredToken = () => {
    return window.localStorage.getItem("tokens")
      ? JSON.parse(window.localStorage.getItem("tokens") || "")
      : "";
  };

  const tryRefresh = async () => {
    let token = getStoredToken();
    if (token && token.access) {
      try {
        const response = await axios.get(refreshTokenURL, { headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token.access}`
       } });
        if(response.status == 200){
          return true;
        }
        else{
          return false;
        }
      } catch (error) {
        console.error("Error refreshing token:", error);
        window.localStorage.removeItem("tokens");
        window.localStorage.removeItem("user");
        window.localStorage.removeItem("currentWorkspaceId");
        Cookies.remove('slack_access_token', { path: '/' });
        Cookies.remove('azuread_token', { path: '/' });
        Cookies.remove('fireflies_access_token', { path: '/' });
        Cookies.remove('csrftoken', { path: '/' });
        return false;
      }
    } else {
      console.warn("No valid token found in localStorage.");
      window.localStorage.removeItem("tokens");
      window.localStorage.removeItem("user");
      return false;
    }
  };

  const logout = async () => {
    let currentToken = getStoredToken();
    await axios.post(logoutURL, { refresh_token : currentToken.refresh },
      {
        headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${currentToken.access}`
         },
        withCredentials: true
    }
    )
      .catch((error) => {
        window.localStorage.removeItem("tokens")
        window.localStorage.removeItem("user");
        alert(error.response.data.detail);
      });
    window.localStorage.removeItem("tokens")
    window.localStorage.removeItem("user");
    window.localStorage.removeItem("currentWorkspaceId");
    Cookies.remove('azuread_token', { path: '/' });
    Cookies.remove('slack_access_token', { path: '/' });
    Cookies.remove('fireflies_access_token', { path: '/' });
    Cookies.remove('csrftoken', { path: '/' });
  };

  const getCookie = (name: string): string | null => {
    let cookieValue: string | null = null;
    if (document.cookie && document.cookie !== '') {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            if (cookie.substring(0, name.length + 1) === (name + '=')) {
                cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                break;
            }
        }
    }
    return cookieValue;
  };

  return (
    <AuthContext.Provider
      value={{ login, logout, getUser, getStoredToken, tryRefresh, getCookie }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};